import * as React from 'react';
import { callFunction } from '../utils/FirebaseUtils';
import { storeDispatch } from '../state/Store';
import * as Actions from '../state/actions';
import { reportBFError } from '../utils/Tracker';
import { Overlay } from '../components/Overlay';
import firebase from 'firebase/app';
import 'firebase/auth';

const drift = (window as any).drift;

enum CancellationReason {
  Undefined,
  NotUsing,
  NotGoodResults,
  NotWorking,
  NotGoodEnough,
  NotCancelling
}

const getReasonLabel = (type: CancellationReason): String => {
  switch (type) {
    case CancellationReason.NotUsing:
      return 'I’m not using the product as much as I thought I will.';
    case CancellationReason.NotGoodResults:
      return 'I can’t make interesting/useful videos with it.';
    case CancellationReason.NotWorking:
      return 'The app doesn’t work properly on my computer.';
    case CancellationReason.NotGoodEnough:
      return 'I prefer using another product.';
    case CancellationReason.NotCancelling:
      return 'Don’t panic, I only need to change payment details.';
    default:
      return '';
  }
};

const LinkFacebook: React.FunctionComponent = props => {
  return (
    <a
      href="https://www.facebook.com/groups/854273858296665/"
      target="_blank"
      rel="noopener noreferrer"
    >
      {props.children || 'Facebook'}
    </a>
  );
};

const LinkEmail: React.FunctionComponent = props => {
  return (
    <a
      href="mailto:info@beatflyer.com"
      target="_blank"
      rel="noopener noreferrer"
    >
      {props.children || 'email'}
    </a>
  );
};

const openChat = () => {
  if (drift) {
    drift.on('ready', (api: any) => {
      api.sidebar.open();
    });
  } else {
    alert('Ops, chat not available now, please use any other contact channel.');
  }
};

const LinkChat: React.FunctionComponent = props => {
  return <a onClick={openChat}>{props.children || 'chat'}</a>;
};

interface State {
  reason: CancellationReason;
  isCancelling: boolean;
}

export default class extends React.Component<{}, State> {
  textInput: HTMLTextAreaElement;
  state = {
    reason: CancellationReason.Undefined,
    isCancelling: false
  };

  didChooseReason = (reason: CancellationReason) => {
    if (this.state.isCancelling) {
      return;
    }
    this.setState({
      reason
    });
  };

  doContinue = () => {
    var reason = '';
    if (this.state.reason !== CancellationReason.Undefined) {
      reason += getReasonLabel(this.state.reason);
      if (this.textInput) {
        reason += ': ' + this.textInput.value;
      }
    }
    this.setState({ isCancelling: true });
    callFunction('deleteAccount', reason)
      .then(() => {
        storeDispatch(Actions.closeModal());
        return firebase.auth().signOut();
      })
      .catch((e: Error) => {
        reportBFError(e);
      });
  };

  componentDidUpdate() {
    if (this.textInput) {
      this.textInput.focus();
    }
  }

  render() {
    const keys = Object.keys(CancellationReason).filter(
      k => typeof CancellationReason[k as any] === 'number'
    );
    keys.shift();

    const verb = 'Delete';
    const progressLabel = 'Deleting...';

    const textInput = (
      <textarea
        ref={el => (this.textInput = el!)}
        placeholder="Your honest feedback"
        disabled={this.state.isCancelling}
      />
    );
    const primaryButton = (
      <button
        className="large primary"
        onClick={this.doContinue}
        disabled={this.state.isCancelling}
      >
        {this.state.isCancelling ? progressLabel : verb}
      </button>
    );
    const primaryButtonAnyway = (
      <button
        className="large primary"
        onClick={this.doContinue}
        disabled={this.state.isCancelling}
      >
        {this.state.isCancelling ? progressLabel : verb + ' anyway'}
      </button>
    );

    let content = <div />;
    switch (this.state.reason) {
      case CancellationReason.Undefined:
        content = (
          <>
            <header>
              <h1>Can you share your feedback?</h1>
            </header>
            <p>
              Knowing why you decided to leave will help us making the product
              better for you ❤️
            </p>
            <ul>
              {keys.map((key, index) => {
                const type = CancellationReason[key];
                return (
                  <li key={index} onClick={() => this.didChooseReason(type)}>
                    {getReasonLabel(type)}
                  </li>
                );
              })}
            </ul>
            <button
              className="skip"
              onClick={this.doContinue}
              disabled={this.state.isCancelling}
            >
              {this.state.isCancelling ? progressLabel : 'Skip'}
            </button>
          </>
        );
        break;
      case CancellationReason.NotUsing:
        content = (
          <>
            <p>
              That’s a shame, other customers are seeing substantial increase in
              engagement from their audience using Beatflyer.
              <br />
              Don’t forget you can always buy a <strong>day pass</strong> next
              time.
            </p>
            <p>Anything else you can tell us about your experience?</p>
            {textInput}
            {primaryButton}
          </>
        );
        break;
      case CancellationReason.NotGoodResults:
        content = (
          <>
            <p>
              You can always reach to us via <LinkChat />, <LinkEmail /> and{' '}
              <LinkFacebook />, to get help in making the best out of Beatflyer.
            </p>
            <p>Anything else you can tell us?</p>
            {textInput}
            {primaryButtonAnyway}
          </>
        );
        break;
      case CancellationReason.NotWorking:
        content = (
          <>
            <p>We managed to make it work on a 10 years old Mac! 🤯</p>
            <p>
              Please reach to us via <LinkChat />, <LinkEmail /> or{' '}
              <LinkFacebook />, to see if we can make it work on yours too.
            </p>
            {primaryButtonAnyway}
          </>
        );
        break;
      case CancellationReason.NotGoodEnough:
        content = (
          <>
            <p>
              Because we are a small team we can quickly add new features.
              <br />
              If there is something you need, please{' '}
              <LinkEmail>do let us know</LinkEmail>!
            </p>
            <p>
              What can you tell us about the product you're planning to use and
              your workflow?
            </p>
            {textInput}
            {primaryButtonAnyway}
          </>
        );
        break;
      case CancellationReason.NotCancelling:
        content = (
          <>
            <p>
              You can change your subscription and payment method clicking the
              Manage button in the previous screen.
            </p>
            <p>
              If you have problems please feel free to{' '}
              <LinkEmail>contact us</LinkEmail>, we should be able to do this
              without the need of closing and re-opening the account.
            </p>
            {primaryButtonAnyway}
          </>
        );
        break;
      default:
        content = <div />;
    }
    return (
      <Overlay>
        <div className="cancel-feedback">{content}</div>
      </Overlay>
    );
  }
}



// WEBPACK FOOTER //
// ./src/feedback/FeedbackCancel.tsx